// Auswertung der Vorgänge für das Admin-Dashboard. Die eingesparte Zeit wird nie
// gespeichert, sondern immer neu berechnet: Anzahl Bilder × Minuten pro Bild.
import type { StatsEvent } from "./api";

export type Period = "month" | "30d" | "year" | "all";

export const PERIOD_LABELS: Record<Period, string> = {
  month: "Dieser Monat",
  "30d": "Letzte 30 Tage",
  year: "Dieses Jahr",
  all: "Gesamt",
};

export function filterByPeriod(events: StatsEvent[], period: Period, now = new Date()): StatsEvent[] {
  if (period === "all") return events;
  const from =
    period === "month"
      ? new Date(now.getFullYear(), now.getMonth(), 1)
      : period === "year"
        ? new Date(now.getFullYear(), 0, 1)
        : new Date(now.getTime() - 30 * 86_400_000);
  return events.filter((e) => new Date(e.ts) >= from);
}

export interface Totals {
  runs: number;
  images: number;
  minutesSaved: number;
  people: number;
}

export function totals(events: StatsEvent[], minutesPerImage: number): Totals {
  const images = sum(events, (e) => e.images);
  return {
    runs: events.length,
    images,
    minutesSaved: images * minutesPerImage,
    people: new Set(events.map((e) => e.userEmail)).size,
  };
}

export interface PersonRow {
  email: string;
  name: string;
  runs: number;
  images: number;
  minutesSaved: number;
  lastTs: string;
}

export function byPerson(events: StatsEvent[], minutesPerImage: number): PersonRow[] {
  const map = new Map<string, PersonRow>();
  for (const e of events) {
    const row = map.get(e.userEmail) ?? { email: e.userEmail, name: e.userName, runs: 0, images: 0, minutesSaved: 0, lastTs: e.ts };
    row.runs += 1;
    row.images += e.images;
    row.minutesSaved = row.images * minutesPerImage;
    if (e.ts > row.lastTs) row.lastTs = e.ts;
    if (!row.name && e.userName) row.name = e.userName;
    map.set(e.userEmail, row);
  }
  return [...map.values()].sort((a, b) => b.images - a.images);
}

export interface MonthRow {
  month: string; // JJJJ-MM
  runs: number;
  images: number;
  minutesSaved: number;
  people: number;
}

export function byMonth(events: StatsEvent[], minutesPerImage: number): MonthRow[] {
  const groups = new Map<string, StatsEvent[]>();
  for (const e of events) {
    const d = new Date(e.ts);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([month, list]) => ({ month, ...totals(list, minutesPerImage) }));
}

// "3 Std. 20 Min." – kurze, gut lesbare Zeitangabe.
export function formatMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} Min.`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} Std. ${rest} Min.` : `${h} Std.`;
}

export function formatMonth(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

export const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

export const SOURCE_LABELS: Record<StatsEvent["sourceType"], string> = {
  sharepoint: "SharePoint",
  local: "Lokaler Ordner",
  single: "Einzelbild",
};

// ---- Export ----------------------------------------------------------------

export function toCsv(events: StatsEvent[], minutesPerImage: number): string {
  const head = ["Datum", "Name", "E-Mail", "Objekt", "Quelle", "Bilder", "davon Titelbilder", "Fehler", "Dauer (s)", "Eingesparte Minuten"];
  const rows = events.map((e) => [
    formatDateTime(e.ts),
    e.userName,
    e.userEmail,
    e.objectName,
    SOURCE_LABELS[e.sourceType],
    e.images,
    e.titleImages,
    e.errors,
    Math.round(e.durationSeconds),
    decimal(e.images * minutesPerImage),
  ]);
  // Semikolon + BOM: öffnet in deutschem Excel direkt korrekt.
  return "﻿" + [head, ...rows].map((r) => r.map(csvCell).join(";")).join("\r\n");
}

export function toMarkdown(events: StatsEvent[], minutesPerImage: number, periodLabel: string): string {
  const t = totals(events, minutesPerImage);
  const lines = [
    `# Bildskalierung – Nutzungsstatistik (${periodLabel})`,
    "",
    `Stand: ${new Date().toLocaleString("de-DE")} · Berechnung: ${decimal(minutesPerImage)} Min. pro Bild`,
    "",
    `- **Skalierte Bilder:** ${t.images}`,
    `- **Eingesparte Zeit:** ${formatMinutes(t.minutesSaved)}`,
    `- **Vorgänge:** ${t.runs}`,
    `- **Aktive Personen:** ${t.people}`,
    "",
    "## Pro Person",
    "",
    "| Name | E-Mail | Vorgänge | Bilder | Eingesparte Zeit | Zuletzt |",
    "|---|---|---:|---:|---:|---|",
    ...byPerson(events, minutesPerImage).map(
      (p) => `| ${md(p.name)} | ${md(p.email)} | ${p.runs} | ${p.images} | ${formatMinutes(p.minutesSaved)} | ${formatDateTime(p.lastTs)} |`,
    ),
    "",
    "## Pro Monat",
    "",
    "| Monat | Vorgänge | Bilder | Eingesparte Zeit | Personen |",
    "|---|---:|---:|---:|---:|",
    ...byMonth(events, minutesPerImage).map(
      (m) => `| ${formatMonth(m.month)} | ${m.runs} | ${m.images} | ${formatMinutes(m.minutesSaved)} | ${m.people} |`,
    ),
    "",
    "## Verlauf",
    "",
    "| Datum | Person | Objekt | Quelle | Bilder |",
    "|---|---|---|---|---:|",
    ...events.map(
      (e) => `| ${formatDateTime(e.ts)} | ${md(e.userName || e.userEmail)} | ${md(e.objectName)} | ${SOURCE_LABELS[e.sourceType]} | ${e.images} |`,
    ),
    "",
  ];
  return lines.join("\n");
}

const sum = (list: StatsEvent[], f: (e: StatsEvent) => number) => list.reduce((a, e) => a + f(e), 0);
const decimal = (n: number) => String(Math.round(n * 100) / 100).replace(".", ",");
const md = (s: string) => s.replace(/\|/g, "\\|");

function csvCell(v: string | number): string {
  let s = String(v);
  // Formel-Injektion in Excel verhindern (Objektnamen stammen aus Ordnernamen).
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  return /[";\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
